import './task.scss';

import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import {
    Item,
    ItemDataStore,
    itemSelectors,
    itemSorters,
    Movement,
    MovementDataStore,
    MovementTypes,
    Task,
    TaskDataStore,
    taskSelectors,
    ThesaurusTerm,
    ThesaurusTermDataStore,
    thesaurusTermSelectors,
} from '@skinsoft/entity';
import { TaskFeaturedCard } from '@skinsoft/entity-ui';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { Featured, ItemCard, PullToRight } from '@skinsoft/ui';
import { combineLatest } from 'rxjs';
import { Component, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';

import {
    CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC,
    CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC,
    REJECT_TASK_TOPIC,
    START_TASK_TOPIC,
} from '../../../busTopics';
import { router, Routes } from '../../../router';
import { CommentEditor } from '../../component/comment-editor';

@Component({
    components: {
        sCommentEditor: CommentEditor,
        sItemCard: ItemCard,
        sPullToRight: PullToRight,
        sTaskFeaturedCard: TaskFeaturedCard,
    },
    template: require('./task.html'),
})
export class TaskPage extends ReactiveVue {
    public $itemDataStore: ItemDataStore;
    public $movementDataStore: MovementDataStore;
    public $route: Route;
    public $taskDataStore: TaskDataStore;
    public $thesaurusTermDataStore: ThesaurusTermDataStore;
    public angleDownIcon = faAngleDown;
    public angleRightIcon = faAngleRight;
    public exclamationTriangleIcon = faExclamationTriangle;
    public itemSelectors = itemSelectors;
    public spinnerIcon = faCircleNotch;

    public hasPendingMovement = false;
    public items: Item[] = [null, null, null] as any[];
    public task: Task = null as any;
    public thesaurusTerm: ThesaurusTerm = null as any;
    public showCollapse = false;
    public operatorCommentPersisting = false;

    public $refs: {
        fakeEntity: HTMLDivElement;
        item: ItemCard[];
        puller: PullToRight;
        ribbon: HTMLDivElement;
        viewport: HTMLDivElement;
    };

    get description() {
        return this.thesaurusTerm ? thesaurusTermSelectors.preferredTerm(this.thesaurusTerm) : null;
    }

    get precisionRequest() {
        return taskSelectors.precisionRequest(this.task);
    }

    get operatorComment() {
        return taskSelectors.operatorComment(this.task, this.$i18n.locale);
    }

    public mounted() {
        this.subscribeToBus(CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC, () =>
            this.createMovementFromTask(MovementTypes.SIMPLE),
        );
        this.subscribeToBus(CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC, () =>
            this.createMovementFromTask(MovementTypes.COMPLEX),
        );
        this.subscribeToBus(REJECT_TASK_TOPIC, () => this.rejectTask());
        this.subscribeToBus(START_TASK_TOPIC, () => this.startTask());
        this.subscribeToEntity();
    }

    public rejectTask() {
        // @TODO implements
    }

    public startTask() {
        // @TODO implements
    }

    public subscribeToEntity() {
        this.subscribeTo(
            this.$taskDataStore.fetchOne(this.$route.params.id),
            task => {
                this.task = task;
            },
            'task',
        );
    }

    public subscribeToItems() {
        const linkableItems = taskSelectors.linkableItems(this.task);

        if (linkableItems.length === 0) {
            this.items = [];
            return;
        }

        this.subscribeTo(
            combineLatest(
                ...linkableItems.map(item => this.$itemDataStore.fetchOne(item.linkableItem)),
            ),
            items => {
                this.items = items.sort(itemSorters.byRealTimeLocation) as Item[];
            },
            'items',
        );
    }

    public subscribeToThesaurusTerm() {
        const id = taskSelectors.associatedTasks(this.task)[0];

        if (!id) {
            this.thesaurusTerm = null as any;
            return;
        }

        this.subscribeTo(
            this.$thesaurusTermDataStore.fetchOne(id),
            thesaurusTerm => {
                this.thesaurusTerm = thesaurusTerm;
            },
            'thesaurusTerm',
        );
    }

    @Watch('$route', { deep: true })
    public onRouteChange() {
        // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
        this.items = [null, null, null] as any[];
        this.$refs.puller.reset();

        this.unsubscribeFrom('task');
        this.subscribeToEntity();
    }

    @Watch('task', { deep: true })
    public onEntityChange() {
        this.unsubscribeFrom('items');
        this.subscribeToItems();
        this.unsubscribeFrom('thesaurusTerm');
        this.subscribeToThesaurusTerm();
    }

    public async onCommentEditorChange(operatorComment: string) {
        this.operatorCommentPersisting = true;
        try {
            await this.$taskDataStore.updateOperatorComment(this.task.id, operatorComment, this.$i18n.locale);
        } finally {
            this.operatorCommentPersisting = false;
        }
    }

    public onPulled() {
        // the user has reach pull-right non return point, we must transition to the next entity
        const next = this.$route.query.next as any;
        const id = (next as string[]).shift() as string;

        requestAnimationFrame(() =>
            router.push({
                name: this.$route.name || "",
                params: { id },
                query: (next.length > 0 ? { next } : {}) as Record<string, string>,
            }),
        );
    }

    public toggleCollapse([_, event]: [Featured, MouseEvent]) {
        if (event.target && (event.target as HTMLElement).id === 'operatorComment') {
            return;
        }

        this.showCollapse = !this.showCollapse;
    }

    private async createMovementFromTask(
        movementType: MovementTypes.SIMPLE | MovementTypes.COMPLEX = MovementTypes.SIMPLE,
    ) {
        this.waitForUserApproval(this.$t('entity.createMovementFromTask') as string);
        this.hasPendingMovement = true;

        let movement: Movement;
        try {
            movement = await (this.$movementDataStore as MovementDataStore).createMovement(
                movementType,
                this.task.id,
            );
        } finally {
            this.hasPendingMovement = false;
        }

        if (movement) {
            router.push({
                name: Routes.MOVEMENT,
                params: { id: movement.id },
            });
        }
    }
}
